import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, Loader2, Eye, EyeOff, Mail, Key, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailSettings = () => {
  usePageTitle('Email Settings');
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  const [settings, setSettings] = useState({
    api_key_masked: '',
    api_key_set: false,
    from_email: '',
    from_name: '',
    updated_at: null,
    updated_by: null
  });
  
  const [formData, setFormData] = useState({
    api_key: '', // Only sent if user enters a new key
    from_email: '',
    from_name: ''
  });
  
  const [apiKeyChanged, setApiKeyChanged] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/email-settings/transactional`);
      setSettings(response.data);
      setFormData({
        api_key: '',
        from_email: response.data.from_email || '',
        from_name: response.data.from_name || ''
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        from_email: formData.from_email,
        from_name: formData.from_name
      };
      
      // Only include API key if it was changed
      if (apiKeyChanged && formData.api_key) {
        updateData.api_key = formData.api_key;
      }
      
      const response = await axios.put(`${API_URL}/api/admin/email-settings/transactional`, updateData);
      setSettings(response.data);
      setFormData(prev => ({ ...prev, api_key: '' }));
      setApiKeyChanged(false);
      toast.success('Email settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#2C9DE1]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin-settings')}
          className="hover:bg-slate-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email Settings
          </h1>
          <p className="text-slate-500 mt-1">
            Configure Resend API settings for transactional emails (account creation, password reset, etc.)
          </p>
        </div>
      </div>

      {/* Settings Card */}
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Mail className="w-5 h-5 text-[#2C9DE1]" />
            Transactional Email Configuration
          </CardTitle>
          <CardDescription>
            These settings are used for system emails like account creation, password reset, and notifications.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* API Key */}
          <div className="space-y-2">
            <Label htmlFor="api_key" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              Resend API Key
            </Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="api_key"
                  type={showApiKey ? 'text' : 'password'}
                  placeholder={settings.api_key_set ? settings.api_key_masked : 'Enter API key...'}
                  value={formData.api_key}
                  onChange={(e) => {
                    setFormData({ ...formData, api_key: e.target.value });
                    setApiKeyChanged(true);
                  }}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              {settings.api_key_set 
                ? 'API key is configured. Enter a new key to replace it.' 
                : 'No API key configured. Enter your Resend API key.'}
            </p>
          </div>

          {/* From Email */}
          <div className="space-y-2">
            <Label htmlFor="from_email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              From Email
            </Label>
            <Input
              id="from_email"
              type="email"
              placeholder="noreply@example.com"
              value={formData.from_email}
              onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
            />
            <p className="text-xs text-slate-500">
              The email address that will appear as the sender for system emails.
            </p>
          </div>

          {/* From Name */}
          <div className="space-y-2">
            <Label htmlFor="from_name" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              From Name
            </Label>
            <Input
              id="from_name"
              type="text"
              placeholder="EMP Medic"
              value={formData.from_name}
              onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
            />
            <p className="text-xs text-slate-500">
              The name that will appear as the sender for system emails.
            </p>
          </div>

          {/* Last Updated Info */}
          <div className="pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Last updated: {formatDate(settings.updated_at)} {settings.updated_by && `by ${settings.updated_by}`}
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="max-w-2xl border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900">About Transactional Emails</h4>
              <p className="text-sm text-blue-700 mt-1">
                These settings control emails sent for:
              </p>
              <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
                <li>New user account creation</li>
                <li>Password reset requests</li>
                <li>Account verification</li>
                <li>System notifications</li>
              </ul>
              <p className="text-sm text-blue-700 mt-2">
                For email marketing settings, go to{' '}
                <button 
                  onClick={() => navigate('/email-marketing')}
                  className="text-blue-800 underline hover:text-blue-900"
                >
                  Email Marketing
                </button>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettings;
